import { Modal, Radio } from "antd";
import Button from "Components/Button";
import React, { useState } from "react";
import { SearchBox } from "../SearchBox";
import {
  Customer,
  useGetCustomerQuery,
} from "hooks/CustomerManagement/useGetCustomerQuery";
import { useDebounce } from "hooks/useDebounce";
import Loader from "Components/Loader";
import { Icon } from "@iconify-icon/react";
import { cn } from "utils";
import { ellipse } from "utils/ellipse";

type ManualOrderCustomerSelectionProps = {
  selectedCustomer: Customer | null;
  setSelectedCustomer: (customer: Customer | null) => void;
};

export const ManualOrderCustomerSelection = ({
  selectedCustomer,
  setSelectedCustomer,
}: ManualOrderCustomerSelectionProps) => {
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");

  const debouncedSearch = useDebounce(customerSearch);

  const { data, isPending } = useGetCustomerQuery({
    search: debouncedSearch,
  });

  const customers = data?.data.docs || [];

  const getCustomerById = (id: string) => {
    return customers.find((customer) => customer.id === id) || null;
  };

  const getCustomerFullName = () => {
    return `${selectedCustomer?.firstName ?? ""} ${
      selectedCustomer?.lastName ?? ""
    }`;
  };

  const onModalClose = (clearCustomer = false) => {
    setShowCustomerModal(false);
    if (clearCustomer) {
      setSelectedCustomer(null);
    }
  };

  return (
    <div>
      <button
        className="space-x-1 flex items-center outline-none border-none"
        onClick={() => setShowCustomerModal(true)}>
        {selectedCustomer ? getCustomerFullName() : "Select"}
        <Icon
          icon="iconamoon:arrow-down-2-duotone"
          width={20}
          color="#474747"
        />
      </button>
      <Modal
        title="Customer"
        open={showCustomerModal}
        className="max-w-[40rem] !w-full"
        onCancel={() => onModalClose()}
        footer={
          <Button
            className={`text-white p-[12px] w-full font-bold rounded-md ease-in-out duration-300`}
            label={"Continue"}
            disabled={!selectedCustomer}
            onClick={() => onModalClose()}
          />
        }>
        <div className="w-full">
          <div className="w-full grid justify-between grid-cols-[1fr_auto] gap-x-2">
            <SearchBox
              value={customerSearch}
              onChange={setCustomerSearch}
              placeholder="Search Name, ID, Email Address"
              className="rounded-xl bg-[#F3F3F3]"
            />
            <Button
              label="Add New Customer"
              className="px-3 py-4 rounded-xl max-h-[44px]"
            />
          </div>

          {isPending && <Loader />}

          {/* list */}
          <Radio.Group
            value={selectedCustomer?.id}
            onChange={(e) =>
              setSelectedCustomer(getCustomerById(e.target.value))
            }
            className="w-full"
            name="customer">
            <div className="max-h-[70vh] overflow-y-auto py-4 w-full">
              {customers.map((customer) => (
                <Radio
                  key={customer.id}
                  value={customer.id}
                  className={cn(
                    "flex items-center space-x-2 border-b border-border px-4 py-3",
                    customer.id === selectedCustomer?.id
                      ? "bg-primary-light"
                      : ""
                  )}>
                  <div className="flex items-center space-x-3">
                    {/* Avatar */}
                    {customer.profileImage ? (
                      <img
                        src={customer.profileImage}
                        alt={customer.firstName}
                        className="w-11 h-11 rounded-full object-cover"
                      />
                    ) : (
                      <div className="size-11 rounded-full bg-gray font-bold justify-center items-center flex bg-primary/20">
                        {customer.firstName[0].toUpperCase()}
                        {customer.lastName[0].toUpperCase()}
                      </div>
                    )}

                    {/* User Details */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium">
                        {customer.firstName} {customer.lastName}
                      </h3>
                      <p className="text-sm text-gray-500 font-normal">
                        ID: #
                        {ellipse(customer.id, customer.email ? 12 : undefined)}
                        {customer.email ? `, ${customer.email}` : null}
                      </p>
                    </div>
                  </div>
                </Radio>
              ))}
            </div>
          </Radio.Group>
        </div>
      </Modal>
    </div>
  );
};
